import { useMemo } from 'react';

import { useListAvailableUserProfiles, UserProfileDTO } from '@/api/auth';
import { useListTenants } from '@/api/trafikportalen';
import { useConfig, useRoleIds } from '@/contexts/ConfigContext';
import { getSelectableUserProfiles } from '@/helpers/userProfiles';
import { useChangeProfile } from '@/hooks/useChangeProfile';
import { useAuth } from '@/stores/auth';
import { useParams, useSearch } from '@tanstack/react-router';
import { FormattedMessage } from 'react-intl';

import { ProfileButton } from './Menu/ProfileButton';

interface TenantProfiles {
  id: string;
  profiles: UserProfileDTO[];
}

export function ProfilePicker({
  onSelect,
  returnTo,
}: {
  onSelect?: (profile: UserProfileDTO) => void;
  returnTo?: string;
}) {
  const changeProfile = useChangeProfile();
  const { userProfile } = useAuth();
  const { data: tenants } = useListTenants();
  const { tenantId: routeTenantId } = useParams({ strict: false });
  const { tenantId: searchTenantId } = useSearch({ strict: false });
  // During login the picker renders on /profiles before a profile is selected,
  // so fall back to the tenant carried in the URL: the route param when nested
  // under $tenantId, otherwise the ?tenantId search param set by the auth
  // redirect.
  const tenantId = userProfile?.tenant_id ?? routeTenantId ?? searchTenantId;
  const roleIds = useRoleIds();
  const config = useConfig();

  const { data: profiles } = useListAvailableUserProfiles({
    application_uri: config.auth.applicationUri,
  });

  const groups = useMemo(() => {
    if (!profiles || !tenants) return [];

    const validProfiles = getSelectableUserProfiles(profiles, tenants, roleIds);

    const groupedProfiles = validProfiles.reduce<TenantProfiles[]>(
      (acc, profile) => {
        const existingGroup = acc.find(
          (group) => group.id === profile.tenant_id,
        );
        if (existingGroup) {
          existingGroup.profiles.push(profile);
        } else {
          acc.push({ id: profile.tenant_id, profiles: [profile] });
        }
        return acc;
      },
      [],
    );
    return groupedProfiles.sort((a, b) => {
      // Sort by tenant ID first - current tenant goes first
      if (a.id === tenantId) return -1;
      if (b.id === tenantId) return 1;

      // Then sort alphabetically by tenant name
      const aName = a.profiles[0].tenant;
      const bName = b.profiles[0].tenant;
      return aName.localeCompare(bName);
    });
  }, [profiles, tenantId, tenants, roleIds]);

  return (
    <ul className="flex w-full flex-col items-center gap-6">
      {groups.map((group, index) => (
        <div key={group.id} className="flex w-full flex-col gap-2">
          {groups.length > 1 && (
            <>
              <span className="text-2xl">{group.profiles[0].tenant}</span>
              {index === 0 && group.id === tenantId && (
                <p className="font-bold">
                  <FormattedMessage defaultMessage="Nuvarande instans" />
                </p>
              )}
            </>
          )}
          <ul className="flex flex-col items-center gap-2">
            {group.profiles.map((profile) => (
              <li key={profile.id} className="flex w-full min-w-0">
                <ProfileButton
                  profile={profile}
                  selected={userProfile?.id === profile.id}
                  onClick={async () => {
                    await changeProfile(profile, { reload: true, returnTo });
                    onSelect?.(profile);
                  }}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </ul>
  );
}
